<template>
  <div>
    <v-alert :type="alertType" v-if="alert" class="alert">{{
      alertMessage
    }}</v-alert>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-card outlined>
            <v-toolbar flat>
              <v-toolbar-title class="headline">
                Team Leaderboard
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <date-picker
                v-model="dateRange"
                range
                valueType="date"
                format="MMM D, YYYY"
                placeholder="Date range"
                style="width: 260px;"
              ></date-picker>
            </v-toolbar>
            <v-data-table
              :headers="statHeaders"
              :items="teamStats"
              :loading="statsLoading"
              hide-default-footer
              :items-per-page="-1"
              item-key="userId"
              show-expand
              :expanded.sync="expanded"
              sort-by="totalPoints"
              sort-desc
              no-data-text="No CRM activity or assignments in this range"
            >
              <template v-slot:item.rank="{ index }">
                {{ index + 1 }}
              </template>
              <template v-slot:item.totalPoints="{ item }">
                <v-menu
                  v-if="item.interactionCount > 0"
                  open-on-hover
                  offset-y
                  max-width="380"
                  :close-on-content-click="false"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      class="font-weight-bold primary--text hover-stat"
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ formatPoints(item.totalPoints) }}
                    </span>
                  </template>
                  <v-card>
                    <v-subheader>
                      {{ formatPoints(item.totalPoints) }}
                      {{ item.totalPoints === 1 ? "point" : "points" }}
                    </v-subheader>
                    <v-divider></v-divider>
                    <v-list dense class="stat-menu-list">
                      <v-list-item
                        v-for="event in item.interactionEvents"
                        :key="event.id"
                      >
                        <v-list-item-content>
                          <v-list-item-title>{{
                            eventContactName(event)
                          }}</v-list-item-title>
                          <v-list-item-subtitle>
                            {{ interactionSubtitle(event) }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-menu>
                <span v-else class="font-weight-bold primary--text">
                  {{ formatPoints(item.totalPoints) }}
                </span>
              </template>
              <template v-slot:item.interactionCount="{ item }">
                <v-menu
                  v-if="item.interactionCount > 0"
                  open-on-hover
                  offset-y
                  max-width="380"
                  :close-on-content-click="false"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <span class="hover-stat" v-bind="attrs" v-on="on">
                      {{ item.interactionCount }}
                    </span>
                  </template>
                  <v-card>
                    <v-subheader>
                      {{ item.interactionCount }}
                      {{
                        item.interactionCount === 1
                          ? "interaction"
                          : "interactions"
                      }}
                    </v-subheader>
                    <v-divider></v-divider>
                    <v-list dense class="stat-menu-list">
                      <v-list-item
                        v-for="event in item.interactionEvents"
                        :key="event.id"
                      >
                        <v-list-item-content>
                          <v-list-item-title>{{
                            eventContactName(event)
                          }}</v-list-item-title>
                          <v-list-item-subtitle>
                            {{ interactionSubtitle(event) }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-menu>
                <span v-else>{{ item.interactionCount }}</span>
              </template>
              <template v-slot:item.averagePoints="{ item }">
                {{ formatAveragePoints(item) }}
              </template>
              <template v-slot:item.totalExpense="{ item }">
                {{ formatExpense(item.totalExpense) }}
              </template>
              <template v-slot:item.overdueCount="{ item }">
                <v-menu
                  v-if="item.overdueCount > 0"
                  open-on-hover
                  offset-y
                  max-width="340"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip x-small color="red" dark v-bind="attrs" v-on="on">
                      {{ item.overdueCount }}
                    </v-chip>
                  </template>
                  <v-card>
                    <v-subheader>
                      {{ item.overdueCount }} overdue
                      {{ item.overdueCount === 1 ? "contact" : "contacts" }}
                    </v-subheader>
                    <v-divider></v-divider>
                    <v-list dense class="stat-menu-list">
                      <v-list-item
                        v-for="contact in item.overdueContacts"
                        :key="contact.id"
                      >
                        <v-list-item-content>
                          <v-list-item-title>{{
                            contact.name
                          }}</v-list-item-title>
                          <v-list-item-subtitle>
                            {{ overdueLabel(contact.nextDue) }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-menu>
                <v-chip v-else x-small color="green" dark>
                  {{ item.overdueCount }}
                </v-chip>
              </template>
              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="py-2">
                  <div v-if="item.interactions.length === 0" class="caption">
                    No interactions logged in this range.
                  </div>
                  <v-chip
                    v-for="interaction in item.interactions"
                    :key="interaction.name"
                    class="mr-2 mb-1"
                    small
                    outlined
                  >
                    {{ interaction.name }}: {{ interaction.count }} ({{
                      formatPoints(interaction.points)
                    }}
                    pts)
                  </v-chip>
                </td>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-if="canManageTeamSettings">
        <v-col cols="12" md="5">
          <v-card outlined>
            <v-card-title>Tier Cadences</v-card-title>
            <v-card-text>
              <v-row
                v-for="setting in tierSettingsLocal"
                :key="setting.tier"
                align="center"
                dense
              >
                <v-col cols="4">
                  <strong>Tier {{ setting.tier }}</strong>
                </v-col>
                <v-col cols="5">
                  <v-text-field
                    v-model.number="setting.cadenceDays"
                    type="number"
                    min="1"
                    label="Check-in every (days)"
                    dense
                    hide-details
                  ></v-text-field>
                </v-col>
                <v-col cols="3">
                  <v-btn
                    small
                    color="primary"
                    text
                    @click="saveTierSetting(setting)"
                    >Save</v-btn
                  >
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="7">
          <v-card outlined>
            <v-card-title>Interaction Types &amp; Points</v-card-title>
            <v-data-table
              :headers="eventTypeHeaders"
              :items="eventTypesLocal"
              hide-default-footer
              :items-per-page="-1"
              dense
              :loading="eventTypesLoading"
            >
              <template v-slot:item.points="{ item }">
                <v-text-field
                  v-model.number="item.points"
                  type="number"
                  step="0.5"
                  min="0"
                  dense
                  hide-details
                  style="max-width: 80px;"
                ></v-text-field>
              </template>
              <template v-slot:item.active="{ item }">
                <v-switch
                  v-model="item.active"
                  dense
                  hide-details
                  class="mt-0"
                ></v-switch>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn small color="primary" text @click="saveEventType(item)"
                  >Save</v-btn
                >
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import gql from "graphql-tag";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import format from "date-fns/format";
import startOfMonth from "date-fns/startOfMonth";
import parseISO from "date-fns/parseISO";
import differenceInCalendarDays from "date-fns/differenceInCalendarDays";
import { mapGetters } from "vuex";
import { hasRole } from "../util";

export default {
  components: {
    DatePicker
  },
  data() {
    return {
      alert: false,
      alertType: false,
      alertMessage: false,
      crmTeamStats: [],
      dateRange: [startOfMonth(new Date()), new Date()],
      expanded: [],
      tierSettingsLocal: [],
      eventTypesLocal: [],
      statHeaders: [
        { text: "#", value: "rank", width: "50px", sortable: false },
        { text: "Name", value: "name" },
        { text: "Points", value: "totalPoints", width: "110px" },
        { text: "Interactions", value: "interactionCount", width: "130px" },
        {
          text: "Avg Points / Interaction",
          value: "averagePoints",
          width: "180px"
        },
        { text: "Expense", value: "totalExpense", width: "120px" },
        { text: "Inbound", value: "inboundReferrals", width: "110px" },
        { text: "Assigned Contacts", value: "contactCount", width: "160px" },
        { text: "Overdue", value: "overdueCount", width: "110px" },
        { text: "", value: "data-table-expand" }
      ],
      eventTypeHeaders: [
        { text: "Name", value: "name" },
        { text: "Points", value: "points", width: "110px" },
        { text: "Active", value: "active", width: "90px" },
        { text: "", value: "actions", sortable: false, width: "90px" }
      ]
    };
  },
  apollo: {
    crmTeamStats: {
      query: gql`
        query CrmTeamStats($startDate: Date!, $endDate: Date!) {
          crmTeamStats(startDate: $startDate, endDate: $endDate) {
            user {
              id
              firstName
              lastName
            }
            totalPoints
            totalExpense
            interactionCount
            inboundReferrals
            contactCount
            overdueCount
            overdueContacts {
              id
              name
              nextDue
            }
            interactions {
              name
              count
              points
            }
            interactionEvents {
              id
              points
              occurredAt
              contact {
                id
                name
              }
              contactEventType {
                name
              }
            }
          }
        }
      `,
      variables() {
        return this.queryDates;
      },
      skip() {
        return !this.queryDates;
      },
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true
    },
    tierSettings: {
      query: gql`
        query TierSettings {
          tierSettings {
            id
            tier
            cadenceDays
          }
        }
      `,
      skip() {
        return !this.canManageTeamSettings;
      },
      result({ data }) {
        if (data && data.tierSettings) {
          this.tierSettingsLocal = data.tierSettings.map(t => ({ ...t }));
        }
      }
    },
    contactEventTypes: {
      query: gql`
        query TeamEventTypes {
          contactEventTypes {
            id
            name
            points
            active
          }
        }
      `,
      skip() {
        return !this.canManageTeamSettings;
      },
      result({ data }) {
        if (data && data.contactEventTypes) {
          this.eventTypesLocal = data.contactEventTypes.map(t => ({ ...t }));
        }
      }
    }
  },
  computed: {
    ...mapGetters(["currentUser", "isAdmin"]),
    canManageTeamSettings() {
      if (!this.currentUser) return false;
      return this.isAdmin || hasRole(this.currentUser, "overlord");
    },
    queryDates() {
      let start = this.toQueryDate(
        this.dateRange && this.dateRange[0],
        startOfMonth(new Date())
      );
      let end = this.toQueryDate(
        this.dateRange && this.dateRange[1],
        new Date()
      );
      if (!start || !end) return null;
      return { startDate: start, endDate: end };
    },
    statsLoading() {
      return !!(
        this.$apollo.queries.crmTeamStats &&
        this.$apollo.queries.crmTeamStats.loading
      );
    },
    eventTypesLoading() {
      return !!(
        this.$apollo.queries.contactEventTypes &&
        this.$apollo.queries.contactEventTypes.loading
      );
    },
    teamStats() {
      if (!this.crmTeamStats) return [];
      return this.crmTeamStats.map(s => ({
        ...s,
        userId: s.user.id,
        name: `${s.user.firstName} ${s.user.lastName}`,
        averagePoints: this.averagePoints(s)
      }));
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.refreshTeamStats();
    });
  },
  methods: {
    toQueryDate(value, fallback) {
      let date = value;
      if (typeof value === "string") {
        date = /^\d{4}-\d{2}-\d{2}/.test(value)
          ? parseISO(value.slice(0, 10))
          : new Date(value);
      }
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        date = fallback;
      }
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
      return format(date, "yyyy-MM-dd");
    },
    refreshTeamStats() {
      let query = this.$apollo.queries.crmTeamStats;
      if (!query || query.skip) return;
      if (typeof query.refresh === "function") {
        query.refresh();
        return;
      }
      if (query.observer) query.refetch();
    },
    overdueLabel(nextDue) {
      if (!nextDue) return "";
      let due = parseISO(nextDue);
      let days = differenceInCalendarDays(new Date(), due);
      let ago = days === 1 ? "1 day overdue" : `${days} days overdue`;
      return `Due ${format(due, "MMM d")} · ${ago}`;
    },
    eventContactName(event) {
      return (event.contact && event.contact.name) || "Unknown contact";
    },
    interactionSubtitle(event) {
      let type =
        (event.contactEventType && event.contactEventType.name) || "Unknown";
      let date = this.formatEventDate(event.occurredAt);
      let points = `${this.formatPoints(event.points)} ${
        event.points === 1 ? "pt" : "pts"
      }`;
      return [type, date, points].filter(Boolean).join(" · ");
    },
    formatEventDate(occurredAt) {
      if (!occurredAt) return "";
      let date =
        typeof occurredAt === "string"
          ? parseISO(occurredAt)
          : new Date(occurredAt);
      if (Number.isNaN(date.getTime())) return "";
      return format(date, "MMM d");
    },
    formatPoints(points) {
      if (points == null) return 0;
      return points % 1 === 0 ? points : points.toFixed(1);
    },
    averagePoints(item) {
      if (!item || !item.interactionCount) return 0;
      return item.totalPoints / item.interactionCount;
    },
    formatAveragePoints(item) {
      if (!item || !item.interactionCount) return "—";
      return this.formatPoints(this.averagePoints(item));
    },
    formatExpense(amount) {
      if (amount == null || amount === "") return "—";
      let value = parseFloat(amount);
      if (!Number.isFinite(value) || value <= 0) return "—";
      return value.toLocaleString("en-US", {
        style: "currency",
        currency: "USD"
      });
    },
    showAlert(type, message) {
      this.alert = true;
      this.alertType = type;
      this.alertMessage = message;
      setTimeout(() => {
        this.alert = false;
      }, 3000);
    },
    saveTierSetting(setting) {
      this.$apollo
        .mutate({
          mutation: gql`
            mutation UpdateTierSetting($tier: Int!, $cadenceDays: Int!) {
              updateTierSetting(tier: $tier, cadenceDays: $cadenceDays) {
                id
                tier
                cadenceDays
              }
            }
          `,
          variables: {
            tier: setting.tier,
            cadenceDays: setting.cadenceDays
          }
        })
        .then(() => {
          this.showAlert("success", `Tier ${setting.tier} cadence updated`);
        })
        .catch(error => {
          console.error(error);
          this.showAlert("error", error.message);
        });
    },
    saveEventType(eventType) {
      this.$apollo
        .mutate({
          mutation: gql`
            mutation UpdateContactEventType(
              $contactEventTypeId: Int!
              $name: String
              $points: Float
              $active: Boolean
            ) {
              updateContactEventType(
                contactEventTypeId: $contactEventTypeId
                name: $name
                points: $points
                active: $active
              ) {
                id
                name
                points
                active
              }
            }
          `,
          variables: {
            contactEventTypeId: parseInt(eventType.id, 10),
            name: eventType.name,
            points: eventType.points,
            active: eventType.active
          }
        })
        .then(() => {
          this.showAlert("success", `${eventType.name} updated`);
        })
        .catch(error => {
          console.error(error);
          this.showAlert("error", error.message);
        });
    }
  }
};
</script>

<style scoped lang="scss">
.stat-menu-list {
  max-height: 300px;
  overflow-y: auto;
}
.hover-stat {
  cursor: pointer;
  border-bottom: 1px dotted currentColor;
}
.alert {
  position: fixed;
  left: 50%;
  bottom: 50px;
  transform: translate(-50%, -50%);
  margin: 0 auto;
  z-index: 10;
}
</style>
